import React, { FC, useState, useEffect, useMemo } from 'react';
import { debounce } from 'debounce';
// common components
import { StyledMap, MapControl } from '@ems/client-design-system';
// functions
import {
  useMapRef,
  useMapApis,
  useMapProps,
  useMapConfig,
  useMapCenter,
  useSingleDateTile,
} from 'src/@app/functions/map';
import {
  useMapLayer,
  useInfringementMap,
  useMapTracksFilter,
} from 'src/@app/functions/infringementsOnMap';
import { useLanguageSelectors } from 'src/@app/reducers/languageReducer';
import { useAuthSelectors } from 'src/@auth/reducers/authReducer';
import {
  flyTo,
  useMapHoverOperations,
  useMapHover,
  useMapClick,
  useMapSelection,
  useGeometryRequiredByMap,
} from 'src/utils';
import { OperationPopup } from 'src/components';
import { useInAirPlayback, useNoiseMonitorPlayback, useStaticDbDisplay } from 'src/utils/playback';
import { NoisePopup } from 'src/components/NoisePopup';
// @ts-ignore
import sassVars from 'src/styles/vars.scss';

export const MapContainer: FC<{
  time: string;
  operationId: string;
  operation: any;
  infTypeId: number;
  infringementType: string;
  infringementId: string;
  position: any;
  isPlaybackMode?: boolean;
  currentTime: number;
  inAirData?: any;
  noiseData: any;
  processedNoiseData: any;
  noiseMonitors: any;
  extraIds: number[];
}> = ({
  time,
  operationId,
  operation,
  infTypeId,
  infringementType,
  infringementId,
  position,
  isPlaybackMode = false,
  currentTime,
  inAirData,
  noiseData,
  processedNoiseData,
  noiseMonitors,
  extraIds,
}) => {
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const mapApis: any = useMapApis(mapNode);
  // get map props from config
  const { viewportFromProps, ...mapProps } = useMapProps('2D');

  const [viewport, setViewport] = useState(viewportFromProps);

  useMapCenter(viewport, setViewport, position, operationId);

  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();
  // getting map style layers
  const layers = useMapLayer({ mapApis, mapBoxConfig, maptype: 'infringements' });

  useGeometryRequiredByMap(
    typeof infringementType !== 'undefined' ? [infringementType] : [],
    mapApis,
    mapBoxConfig
  );

  const { dateString, datesArray } = useSingleDateTile(
    mapApis,
    mapBoxConfig,
    layers,
    time,
    operationId
  );

  const { tracks } = useInfringementMap(
    'inf-summary',
    mapApis,
    mapBoxConfig,
    dateString,
    operationId,
    operation,
    infTypeId,
    infringementId,
    infringementType,
    true,
    time,
    extraIds
  );

  useEffect(() => {
    if (mapApis) {
      mapApis.setPaintProperty(
        `${mapBoxConfig.foregroundLayerPrefix}${dateString}`,
        'line-width',
        isPlaybackMode
          ? ['interpolate', ['linear'], ['zoom'], 8, 0.75, 14, 2]
          : ['interpolate', ['linear'], ['zoom'], 8, 0.75, 11, 4, 14, 6]
      );

      // Hide non-selected layers

      mapApis.setLayoutProperty(
        `${mapBoxConfig.backgroundLayerPrefix}${dateString}`,
        'visibility',
        isPlaybackMode ? 'none' : 'visible'
      );

      mapApis.setLayoutProperty(
        `hovered_${dateString}`,
        'visibility',
        isPlaybackMode ? 'none' : 'visible'
      );

      mapApis.setLayoutProperty(
        `infringement_select_${dateString}`,
        'visibility',
        isPlaybackMode ? 'none' : 'visible'
      );
    }
  }, [isPlaybackMode]);

  // extrack map filters for the features
  const { infringementFilter } = useMapTracksFilter(tracks);

  // handle hover and get hovered operation
  const { hoveredOperation, handleHover, setHoveredOperation } = useMapHoverOperations(
    viewport,
    mapApis,
    datesArray,
    infringementFilter
  );
  // set map selection for the hover object
  useMapHover(hoveredOperation, mapApis);

  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  // Map selection
  const [showSelected, setShowSelected] = useState(false);
  const [selectedOperations, setSelectedOperations]: any = useState(null);

  // click handlers that provide selected operations
  const { handleClick, selectedOperation } = useMapClick(
    hoveredOperation,
    setShowSelected,
    mapApis,
    datesArray,
    infringementFilter,
    selectedOperations,
    setSelectedOperations
  );

  // set feature state for the hovered operation
  useMapSelection(selectedOperations, mapApis, 'infringement-select');

  // reset hover and select when selection changes
  useEffect(() => {
    setShowSelected(false);
    setHoveredOperation(null);
    setSelectedOperations([]);
  }, [dateString]);

  // get field labels from language selectors
  const languageSelectors = useLanguageSelectors();
  const {
    fields: { operations: opsFields },
    abbreviations: { operations: opsAbbreviation },
  } = languageSelectors.getLanguage();
  const labels = Object.assign(opsFields, opsAbbreviation);

  const accessToken = useAuthSelectors().getAccessToken();

  // Playback Section
  const dataSources = useMemo(() => (operation ? [operation, ...inAirData] : inAirData), [
    operation,
    inAirData,
  ]);
  useInAirPlayback(mapApis, dataSources, currentTime, isPlaybackMode, Number(operationId));
  const staticMonitors = useStaticDbDisplay(mapApis, noiseMonitors, noiseData, isPlaybackMode);
  const selectedMonitor = useNoiseMonitorPlayback({
    mapApis,
    noiseMonitors,
    noiseData,
    processedNoiseData,
    currentTime,
    isPlaybackMode,
    showNMTDefault: false,
  });

  const noisePopups: JSX.Element[] = [];
  if (selectedMonitor.length > 0 || staticMonitors.length > 0) {
    const mapFn = (monitor: any) => {
      noisePopups.push(
        <NoisePopup
          key={`popup_${monitor.properties.id}`}
          zoomLevel={mapApis.getZoom()}
          monitorData={monitor}
        />
      );
    };
    if (isPlaybackMode) {
      selectedMonitor.map(mapFn);
    } else {
      staticMonitors.map(mapFn);
    }
  }

  // End Playback Section

  return (
    <StyledMap
      {...mapProps}
      viewport={viewport}
      onViewportChange={viewport => {
        viewport.maxPitch = 0;
        setViewport(viewport);
      }}
      ref={mapRef}
      onClick={handleClick}
      onHover={debounce(handleHover, 5)}
      transformRequest={
        mapBoxConfig && mapBoxConfig.transformRequest && mapBoxConfig.transformRequest(accessToken)
      }>
      <MapControl resetView={resetView} />
      {noisePopups}
      {showSelected &&
        selectedOperation &&
        selectedOperation.latitude &&
        selectedOperation.longitude && (
          <OperationPopup
            selectedOperation={selectedOperation}
            onClose={() => setShowSelected(false)}
            labels={labels}
          />
        )}
      {!showSelected &&
        hoveredOperation &&
        hoveredOperation.latitude &&
        hoveredOperation.longitude && (
          <OperationPopup
            selectedOperation={hoveredOperation}
            onClose={() => {
              console.log(close);
            }}
            labels={labels}
          />
        )}
    </StyledMap>
  );
};
